import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Loader2, Copy, Check, QrCode, Smartphone, KeyRound, CircleDot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface TwoFactorSetupProps {
  onComplete?: () => void;
}

type ChecklistStep = 1 | 2 | 3;

const Checklist = ({ current }: { current: ChecklistStep }) => {
  const items = [
    { n: 1 as ChecklistStep, icon: QrCode, label: "Gerar novo QR code" },
    { n: 2 as ChecklistStep, icon: Smartphone, label: "Cadastrar no app autenticador" },
    { n: 3 as ChecklistStep, icon: KeyRound, label: "Validar código de 6 dígitos" },
  ];
  return (
    <ol className="space-y-2 bg-muted/30 rounded-xl p-3">
      {items.map(({ n, icon: Icon, label }) => {
        const done = n < current;
        const active = n === current;
        return (
          <li key={n} className="flex items-center gap-2 text-xs font-body">
            {done ? (
              <Check className="w-4 h-4 text-primary shrink-0" />
            ) : active ? (
              <CircleDot className="w-4 h-4 text-primary shrink-0 animate-pulse" />
            ) : (
              <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <span
              className={
                done
                  ? "text-muted-foreground line-through"
                  : active
                  ? "text-foreground font-medium"
                  : "text-muted-foreground"
              }
            >
              Passo {n}: {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const TwoFactorSetup = ({ onComplete }: TwoFactorSetupProps) => {
  const [step, setStep] = useState<"idle" | "setup" | "verify">("idle");
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const startEnrollment = async () => {
    setLoading(true);
    try {
      setCode("");
      setFactorId("");
      setQrCode("");
      setSecret("");

      // Limpa fatores TOTP não verificados quando a API permite, mas não bloqueia o fluxo
      // caso algum fator antigo não possa ser removido imediatamente.
      const { data: existing } = await supabase.auth.mfa.listFactors();
      const stale = existing?.totp?.filter((f: any) => f.status !== "verified") ?? [];
      if (stale.length > 0) {
        await Promise.allSettled(
          stale.map((f: any) => supabase.auth.mfa.unenroll({ factorId: f.id }))
        );
      }

      let enrollmentData: Awaited<ReturnType<typeof supabase.auth.mfa.enroll>>["data"] = null;
      let lastError: any = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const suffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          friendlyName: `Zappy Admin ${suffix}`,
        });

        if (!error) {
          enrollmentData = data;
          break;
        }

        lastError = error;
        if (!/friendly name|already exists|mfa_factor_name_conflict/i.test(error.message ?? "")) {
          throw error;
        }
      }

      if (!enrollmentData) throw lastError ?? new Error("Não foi possível iniciar o 2FA");

      if (enrollmentData.type !== "totp") {
        throw new Error("Tipo de 2FA inválido para esta configuração");
      }

      setFactorId(enrollmentData.id);
      setQrCode(enrollmentData.totp.qr_code);
      setSecret(enrollmentData.totp.secret);
      setStep("setup");
    } catch (err: any) {
      const message = /friendly name|already exists|mfa_factor_name_conflict/i.test(err?.message ?? "")
        ? "Encontramos uma configuração 2FA antiga. Tente tocar em Configurar 2FA novamente."
        : err?.message || "Erro ao configurar 2FA";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const restartEnrollment = async () => {
    setLoading(true);
    try {
      if (factorId) {
        await supabase.auth.mfa.unenroll({ factorId });
      }
    } catch {
      // Se o fator temporário já expirou/foi removido, segue criando um novo QR.
    } finally {
      setLoading(false);
    }

    setStep("idle");
    await startEnrollment();
  };

  const verifyCode = async () => {
    if (code.length !== 6) {
      toast.error("Digite o código de 6 dígitos");
      return;
    }
    setLoading(true);
    try {
      // Item 49 — rate limit dedicado: 5 tentativas / 15 min
      const { data: allowed, error: rlErr } = await supabase.rpc("check_and_consume_rate_limit", {
        _action: "totp_verify",
        _max: 5,
        _window_seconds: 15 * 60,
      });
      if (rlErr) throw rlErr;
      if (!allowed) {
        toast.error("Muitas tentativas. Aguarde 15 minutos antes de tentar novamente.");
        setLoading(false);
        return;
      }

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;

      // Garante que a sessão local reflita AAL2 imediatamente
      await supabase.auth.refreshSession();

      toast.success("2FA ativado com sucesso! 🔒");
      setStep("idle");
      onComplete?.();
    } catch (err: any) {
      const message = /invalid.*totp|totp.*invalid|code/i.test(err?.message ?? "")
        ? "Código inválido. Use o código do QR code mais recente ou gere um novo QR."
        : err?.message || "Código inválido";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === "idle") {
    return (
      <div className="glass-card p-5 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div>
            <p className="font-body font-medium text-foreground">Autenticação em Dois Fatores (2FA)</p>
            <p className="text-xs font-body text-muted-foreground">Adicione uma camada extra de segurança à sua conta</p>
          </div>
        </div>
        <Button onClick={startEnrollment} disabled={loading} className="w-full rounded-xl" variant="outline">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Configurar 2FA
        </Button>

        <div className="mt-4">
          <p className="text-xs font-body text-muted-foreground mb-2">O que você vai fazer:</p>
          <Checklist current={1} />
          <p className="mt-3 text-[11px] font-body text-muted-foreground leading-relaxed">
            Antes de começar, instale um app autenticador no seu celular: <strong>Google Authenticator</strong>,{" "}
            <strong>Microsoft Authenticator</strong> ou <strong>Authy</strong>.
          </p>
        </div>
      </div>
    );
  }

  if (step === "setup") {
    return (
      <div className="glass-card p-5 mb-4 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="font-body font-medium text-foreground">Configurar 2FA</h3>
        </div>

        <Checklist current={2} />

        <div className="space-y-2 text-xs font-body text-muted-foreground">
          <p className="font-medium text-foreground">Como cadastrar no autenticador:</p>
          <ol className="list-decimal list-inside space-y-1 pl-1">
            <li>Abra o app autenticador (Google Authenticator, Authy, etc.).</li>
            <li>Toque em <strong>+</strong> e escolha <strong>Escanear QR code</strong>.</li>
            <li>Aponte a câmera para o QR abaixo (ou cole a chave manualmente).</li>
            <li>O app vai gerar um código de 6 dígitos que muda a cada 30s.</li>
          </ol>
        </div>

        <div className="flex justify-center bg-qr-surface rounded-xl p-4">
          <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" loading="lazy" decoding="async" />
        </div>

        <div className="space-y-1">
          <p className="text-xs font-body text-muted-foreground">Ou copie a chave manualmente:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-muted/50 rounded-lg px-3 py-2 font-mono text-foreground break-all">
              {secret}
            </code>
            <button onClick={copySecret} className="p-2 rounded-lg hover:bg-muted/50 transition-colors">
              {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Button onClick={() => setStep("verify")} className="w-full rounded-xl">
            Próximo — Verificar Código
          </Button>
          <Button onClick={restartEnrollment} disabled={loading} variant="ghost" className="w-full rounded-xl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Gerar novo QR code
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-5 mb-4 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h3 className="font-body font-medium text-foreground">Verificar Código</h3>
      </div>

      <Checklist current={3} />

      <div className="space-y-1 text-xs font-body text-muted-foreground">
        <p className="font-medium text-foreground">Última etapa:</p>
        <ol className="list-decimal list-inside space-y-1 pl-1">
          <li>Abra o app autenticador e localize a conta <strong>Zappy Admin</strong>.</li>
          <li>Copie o código de 6 dígitos exibido (válido por ~30 segundos).</li>
          <li>Digite abaixo e toque em <strong>Ativar 2FA</strong>.</li>
        </ol>
        <p className="pt-1">⚠️ Se o código falhar, gere um novo QR e refaça o cadastro no app.</p>
      </div>

      <Input
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="000000"
        maxLength={6}
        className="text-center text-2xl tracking-[0.5em] font-mono"
        inputMode="numeric"
      />

      <div className="flex gap-3">
        <Button onClick={() => setStep("setup")} variant="outline" className="flex-1 rounded-xl">
          Voltar
        </Button>
        <Button onClick={verifyCode} disabled={loading || code.length !== 6} className="flex-1 rounded-xl">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Ativar 2FA
        </Button>
      </div>
      <Button onClick={restartEnrollment} disabled={loading} variant="ghost" className="w-full rounded-xl">
        Gerar novo QR code
      </Button>
    </div>
  );
};

export default TwoFactorSetup;
